/* ----------------------------------------------------------------------------
 * Content Library data source (lane bc-content-studio-ux).
 *
 * READ-ONLY: GET /v1/content/library (blogs + social posts in one list, server-
 * side filters, saved-view counts, grouping, paging) and GET
 * /v1/content/library/config (the workspace's content_library_v1 flag).
 * Tenancy and the domain scope are enforced SERVER-side; this module only
 * passes the validated query built by libraryModel.apiQuery. Every mutation in
 * the console reuses the EXISTING per-item helpers (sources-social.ts,
 * sources-content.ts) — there is no new write path here.
 * -------------------------------------------------------------------------- */
import { api } from '@/lib/api';
import type { LibraryView } from '@/pages/social/library/libraryModel';

export interface LibraryWp {
  state: 'none' | 'draft' | 'scheduled' | 'published' | 'failed' | string;
  edit_url: string | null;
  error: string | null;
  scheduled_for: string | null;
  published_at: string | null;
}
export interface LibraryDomain { project_id: number | null; label: string; unassigned: boolean }

interface LibraryBase {
  id: number;
  title: string;
  status: string;
  view: string;
  source: 'manual' | 'aura' | 'aura_auto' | string;
  target_page: string | null;
  keyword: string | null;
  created_at: string;
  updated_at: string;
  scheduled_at: string | null;
  published_at: string | null;
  project_id: number | null;
  domain?: LibraryDomain;
}
export interface LibrarySocialItem extends LibraryBase {
  kind: 'social';
  channel: string;
  format: 'single' | 'carousel';
  pages: number;
  excerpt: string;
  publish_state: string;
  provider_ref_present: boolean;
  parent?: { id: number; title: string };
  /** present on grouped children: does this variant itself match the filters? */
  matches?: boolean;
}
export interface LibraryBlogItem extends LibraryBase {
  kind: 'blog';
  slug: string;
  wp: LibraryWp;
  children: LibrarySocialItem[];
}
export type LibraryItem = LibraryBlogItem | LibrarySocialItem;

export interface LibraryOption { value: string; label?: string; count: number }
export interface LibraryPage {
  scope: string;
  items: LibraryItem[];
  nextCursor: string | null;
  total: number;
  facets: Record<LibraryView | 'published', number>;
  hidden: { failed: number; empty: number };
  options: { target_pages: LibraryOption[]; keywords: LibraryOption[]; channels: LibraryOption[] };
  windowCapped: { blogs: boolean; social: boolean };
}

interface LibraryDto {
  ok?: boolean;
  scope?: string;
  items?: LibraryItem[];
  next_cursor?: string | null;
  total?: number;
  facets?: Record<string, number>;
  hidden?: { failed?: number; empty?: number };
  options?: { target_pages?: LibraryOption[]; keywords?: LibraryOption[]; channels?: LibraryOption[] };
  window_capped?: { blogs?: boolean; social?: boolean };
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export async function fetchLibrary(query: Record<string, string>, signal?: AbortSignal): Promise<LibraryPage> {
  const res = await api.get<LibraryDto>('/content/library', query, signal);
  const facets = res.facets ?? {};
  return {
    scope: typeof res.scope === 'string' ? res.scope : 'none',
    items: Array.isArray(res.items) ? res.items.map((i) => (i.kind === 'blog' ? { ...i, children: Array.isArray(i.children) ? i.children : [] } : i)) : [],
    nextCursor: typeof res.next_cursor === 'string' && res.next_cursor !== '' ? res.next_cursor : null,
    total: num(res.total),
    facets: {
      needs_review: num(facets.needs_review), ready_to_publish: num(facets.ready_to_publish), scheduled: num(facets.scheduled),
      published_month: num(facets.published_month), published: num(facets.published), failed: num(facets.failed),
      empty: num(facets.empty), all: num(facets.all),
    },
    hidden: { failed: num(res.hidden?.failed), empty: num(res.hidden?.empty) },
    options: {
      target_pages: Array.isArray(res.options?.target_pages) ? res.options!.target_pages! : [],
      keywords: Array.isArray(res.options?.keywords) ? res.options!.keywords! : [],
      channels: Array.isArray(res.options?.channels) ? res.options!.channels! : [],
    },
    windowCapped: { blogs: res.window_capped?.blogs === true, social: res.window_capped?.social === true },
  };
}

/** The workspace flag. Any failure reads as OFF so the app shows today's screens. */
export async function fetchLibraryConfig(): Promise<{ contentLibraryV1: boolean }> {
  try {
    const res = await api.get<{ content_library_v1?: boolean }>('/content/library/config');
    return { contentLibraryV1: res.content_library_v1 === true };
  } catch {
    return { contentLibraryV1: false };
  }
}
